import useEmblaCarousel, { UseEmblaCarouselType } from 'embla-carousel-react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import React from 'react';
import type { EmblaOptionsType, EmblaPluginType } from 'embla-carousel';
import type { EmblaViewportRefType } from 'embla-carousel-react';

import { Button } from '@/components/design-system';
import {
  SIZE_SCALE,
  PADDING_X,
  PADDING_Y,
  OVERFLOW,
  ACCESSIBILITY_SCALE,
  FLEX_SHRINK,
  FLEX_GROW,
  FLEX_BASIS,
} from '@/components/design-system/tokens';
import { withMemoryLeakMonitor } from '@/components/ui/MemoryLeakMonitor';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/lib/utils';

interface CarouselContextType {
  carouselRef: EmblaViewportRefType;
  api: UseEmblaCarouselType[1] | undefined;
  opts: EmblaOptionsType | undefined;
  orientation: 'horizontal' | 'vertical';
  scrollPrev: () => void;
  scrollNext: () => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
}

const CarouselContext = React.createContext<CarouselContextType | null>(null);

function useCarousel(): CarouselContextType {
  const context = React.useContext(CarouselContext);

  if (!context) {
    throw new Error('useCarousel must be used within a <Carousel />');
  }

  return context;
}

interface CarouselProps extends React.HTMLAttributes<HTMLDivElement> {
  orientation?: 'horizontal' | 'vertical';
  opts?: EmblaOptionsType;
  setApi?: (api: UseEmblaCarouselType[1]) => void;
  plugins?: EmblaPluginType[];
}

const Carousel = React.forwardRef<HTMLDivElement, CarouselProps>(
  ({ orientation = 'horizontal', opts, setApi, plugins, className, children, ...props }, ref) => {
    const [carouselRef, api] = useEmblaCarousel(
      {
        ...opts,
        axis: orientation === 'horizontal' ? 'x' : 'y',
      },
      plugins
    );
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);

    const onSelect = React.useCallback((api: UseEmblaCarouselType[1] | undefined) => {
      if (!api) {
        return;
      }

      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());
    }, []);

    const scrollPrev = React.useCallback(() => {
      api?.scrollPrev();
    }, [api]);

    const scrollNext = React.useCallback(() => {
      api?.scrollNext();
    }, [api]);

    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          scrollPrev();
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          scrollNext();
        }
      },
      [scrollPrev, scrollNext]
    );

    React.useEffect(() => {
      if (!api || !setApi) {
        return;
      }

      setApi(api);
    }, [api, setApi]);

    React.useEffect(() => {
      if (!api) {
        return;
      }

      onSelect(api);
      api.on('reInit', onSelect);
      api.on('select', onSelect);

      return () => {
        api.off('select', onSelect);
      };
    }, [api, onSelect]);

    return (
      <CarouselContext.Provider
        value={{
          carouselRef,
          api: api,
          opts,
          orientation,
          scrollPrev,
          scrollNext,
          canScrollPrev,
          canScrollNext,
        }}
      >
        <div
          ref={ref}
          onKeyDownCapture={handleKeyDown}
          className={cn('relative', className)}
          role="region"
          aria-roledescription="carousel"
          {...props}
        >
          {children}
        </div>
      </CarouselContext.Provider>
    );
  }
);
Carousel.displayName = 'Carousel';

const CarouselContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const { carouselRef, orientation } = useCarousel();

    return (
      <div ref={carouselRef as React.LegacyRef<HTMLDivElement>} className={`${OVERFLOW.hidden}`}>
        <div
          ref={ref}
          className={cn(
            'flex',
            orientation === 'horizontal' ? '-ml-4' : '-mt-4 flex-col',
            className
          )}
          {...props}
        />
      </div>
    );
  }
);
CarouselContent.displayName = 'CarouselContent';

const CarouselItem = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const { orientation } = useCarousel();

    return (
      <div
        ref={ref}
        role="group"
        aria-roledescription="slide"
        className={cn(
          SIZE_SCALE.minW0,
          FLEX_SHRINK[0],
          FLEX_GROW[0],
          FLEX_BASIS.full,
          orientation === 'horizontal' ? PADDING_X['4'] : PADDING_Y['4'],
          className
        )}
        {...props}
      />
    );
  }
);
CarouselItem.displayName = 'CarouselItem';

interface CarouselPreviousProps extends React.ComponentProps<typeof Button> {
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
  size?: 'default' | 'sm' | 'lg' | 'icon';
}

const CarouselPrevious = React.forwardRef<HTMLButtonElement, CarouselPreviousProps>(
  ({ className, variant = 'outline', size = 'icon', ...props }, ref) => {
    const { orientation, scrollPrev, canScrollPrev } = useCarousel();
    const { t } = useTranslation() as {
      t: (page: string, context: string, fallback?: string) => string;
      currentLanguage: string;
      changeLanguage: (lang: string) => void;
      loading: boolean;
      forceUpdate: number;
      translations: Record<string, unknown>;
      fallbackTranslations: Record<string, unknown>;
      debugTranslations: () => void;
    };

    return (
      <Button
        ref={ref}
        variant={variant}
        size={size}
        className={cn(
          `absolute ${SIZE_SCALE['8']} ${SIZE_SCALE.w8} rounded-full`,
          orientation === 'horizontal'
            ? '-left-12 top-1/2 -translate-y-1/2'
            : '-top-12 left-1/2 -translate-x-1/2 rotate-90',
          className
        )}
        disabled={!canScrollPrev}
        onClick={scrollPrev}
        {...props}
      >
        <ArrowLeft className={`${SIZE_SCALE['4']} ${SIZE_SCALE.w4}`} />
        <span className={`${ACCESSIBILITY_SCALE.srOnly}`}>
          {String(t('common', 'previous_slide', 'Previous slide'))}
        </span>
      </Button>
    );
  }
);
CarouselPrevious.displayName = 'CarouselPrevious';

interface CarouselNextProps extends React.ComponentProps<typeof Button> {
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
  size?: 'default' | 'sm' | 'lg' | 'icon';
}

const CarouselNext = React.forwardRef<HTMLButtonElement, CarouselNextProps>(
  ({ className, variant = 'outline', size = 'icon', ...props }, ref) => {
    const { orientation, scrollNext, canScrollNext } = useCarousel();
    const { t } = useTranslation() as {
      t: (page: string, context: string, fallback?: string) => string;
      currentLanguage: string;
      changeLanguage: (lang: string) => void;
      loading: boolean;
      forceUpdate: number;
      translations: Record<string, unknown>;
      fallbackTranslations: Record<string, unknown>;
      debugTranslations: () => void;
    };

    return (
      <Button
        ref={ref}
        variant={variant}
        size={size}
        className={cn(
          `absolute ${SIZE_SCALE['8']} ${SIZE_SCALE.w8} rounded-full`,
          orientation === 'horizontal'
            ? '-right-12 top-1/2 -translate-y-1/2'
            : '-bottom-12 left-1/2 -translate-x-1/2 rotate-90',
          className
        )}
        disabled={!canScrollNext}
        onClick={scrollNext}
        {...props}
      >
        <ArrowRight className={`${SIZE_SCALE['4']} ${SIZE_SCALE.w4}`} />
        <span className={`${ACCESSIBILITY_SCALE.srOnly}`}>
          {String(t('common', 'next_slide', 'Next slide'))}
        </span>
      </Button>
    );
  }
);
CarouselNext.displayName = 'CarouselNext';

const CarouselWithMonitor = withMemoryLeakMonitor(Carousel, {
  componentName: 'Carousel',
  enableRenderTracking: true,
  enableLeakDetection: true,
  warnOnExcessiveRenders: true,
  renderThreshold: 5,
});

export {
  CarouselWithMonitor as Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
};
